import { Injectable } from '@angular/core';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { filter, first, map, switchMap, take } from 'rxjs/operators';

import {
  basketGroupingChanged,
  clearBasket,
  expandAllBasketLines,
  expandBasketLines,
  expandDefaultBasketLines,
  initExpandDefaultBasketLines,
  manualDiscountsUpdatedFailed,
  rescheduleBasketItem,
  resetBasketItemsSelection,
  resetExpandedLines,
  updateBasketItems,
} from '../actions';
import {
  selectBasketData,
  selectBasketGroupLines,
  selectDefaultExpandedBasketLinesIds,
} from '../selectors';

@Injectable()
export class BasketUiEffects {
  constructor(private actions$: Actions, private store: Store) {}
  public expandLinesOnGroupChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketGroupingChanged),
      switchMap(() => [resetExpandedLines(), expandDefaultBasketLines()]),
    ),
  );

  public expandLinesOnInitLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initExpandDefaultBasketLines),
      switchMap(({ basketId }) =>
        this.store.pipe(
          select(selectBasketData),
          filter((basket) => basket?.basketId === basketId),
          take(1),
        ),
      ),
      map(() => expandDefaultBasketLines()),
    ),
  );

  public expandDefaultBasketLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(expandDefaultBasketLines),
      switchMap(() =>
        this.store.pipe(
          select(selectDefaultExpandedBasketLinesIds),
          first((lines) => lines.length > 0),
        ),
      ),
      map((ids) => expandBasketLines({ ids })),
    ),
  );

  public expandAllBasketLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(expandAllBasketLines),
      concatLatestFrom(() => this.store.select(selectBasketGroupLines)),
      map(([_, items]) =>
        expandBasketLines({ ids: items.map((item) => item.id) }),
      ),
    ),
  );

  public resetSelection = createEffect(() =>
    this.actions$.pipe(
      ofType(rescheduleBasketItem, clearBasket, updateBasketItems),
      map(() => resetBasketItemsSelection()),
    ),
  );

  public showMessageWhenUpdatingDiscountsAtServerFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(manualDiscountsUpdatedFailed),
      map(() => {
        return showSnackBarMessage({
          message: $localize`:Basket|Failed to update discounts basket message@@basketDiscountsUpdatedFailed:Failed to update discounts of basket.`,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );
}
